import { ContactStatus, ContactType } from '@wonderschool/common-base-types';
import { ArrowRightIcon, Button, CloseIcon } from '@wonderschool/common-base-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { isEmpty, omit } from 'lodash';
import PageHeader from '../../Components/Shared/PageHeader';
import { OnSaveCallbackParamsType } from '../../common';
import { saveContact } from '../../contacts/contactsAPI';
import { determineContactStatus } from '../../contacts/determineContactStatus';
import { INVITATION_TYPES } from '../../contacts/enums';
import { EnrollmentSourceEnum, EnrollmentStatusEnum } from '../../enrollments';
import { dateFormatter, formatISOStringAsUtcDateOrNull, isTodayOrBefore } from '../../helpers/dates';
import { formatFullName, phoneNumberFormat } from '../../helpers/utils';
import { usePrimaryLocation } from '../../hooks/useLocations';
import { useOrganization, useOrganizationContactsListener } from '../../hooks/useOrganizations';
import { InterestedFamilyType, MarketplaceFilteredStatusType, useMarketplaceAPI } from '../../integrations';
import { RouteNameEnum, useRoutes } from '../../navigation';
import { studentAdded } from '../../redux/actions/studentsActions';
import { logError, logInfo } from '../../rollbar';
import { MARKETPLACE_REVENUE_SOURCE_MAP, MARKETPLACE_STATUS_MAP } from '../consts';
import { saveStudent } from '../studentsAPI';
import { createScheduledDaysFromInterestedFamily } from '../studentsUtils';
import { EnrollmentInformationType, StudentAndParentsType } from '../types';
import StudentAndParentsForm from './StudentAndParentsForm';
import EnrollmentInformationForm from './enrollment/EnrollmentInformationForm';

type StudentAddPageProps = {
  onClick?: () => void;
};
type OnSaveParamsType = OnSaveCallbackParamsType<EnrollmentInformationType | StudentAndParentsType>;

type StudentSaveDataType = {
  enrollmentInformation?: EnrollmentInformationType;
  studentAndParents?: StudentAndParentsType;
};

// TODO: This form uses a bunch of useState callbacks and is highly coupled, not ideal for a form.
// Migrate this to react-hook-form once we start on the enrollments rebuild in November 2024.
const StudentAddPage: React.FC<StudentAddPageProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [dataToSave, setDataToSave] = useState<StudentSaveDataType>({});
  const [interestedFamily, setInterestedFamily] = useState<InterestedFamilyType>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { gotoRouteByName } = useRoutes();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const params: any = useParams();
  const marketplaceChildId = params.marketplaceChildId ? parseInt(params.marketplaceChildId) : undefined;

  const organization = useOrganization() as any;
  const location = usePrimaryLocation();
  const { goBack } = useRoutes();
  const { sendEnrollmentStatus } = useMarketplaceAPI();

  const roomOptions = useSelector((state: any) => state.rooms?.list || []);

  const defaultRoomId = roomOptions.length === 1 ? roomOptions[0].id : undefined;

  useOrganizationContactsListener(organization.id);

  const contacts = useMemo<any[]>(() => {
    const contactsLocal: any[] = [];
    const room = dataToSave.enrollmentInformation?.room;
    const parents = dataToSave.studentAndParents?.parents ?? [];

    for (const parent of parents) {
      let contact;

      if (parent.contact) {
        contact = parent.contact;
      } else {
        // Mirror the edit-contact flow: classify against the auth user AND any
        // verified/invited contact for this email in other orgs, so an existing
        // parent becomes `verified` (gets the uid linkage + existing-account email)
        // instead of a new create-account invitation. See determineContactStatus.
        const result = determineContactStatus({
          sendInvitation: !!parent.shouldSendInvitation,
          emailUsage: {
            hasAuthUser: !!parent.hasAuthUser,
            uid: parent.uid ?? null,
            existingContacts:
              parent.existingContacts?.map((existingContact) => ({
                id: existingContact.id,
                status: existingContact.status,
                invitationId: existingContact.invitationId,
                uid: existingContact.uid,
              })) ?? [],
          },
          currentContact: {
            status: ContactStatus.new,
            invitationId: null,
          },
        });

        const status = result?.status ?? ContactStatus.new;
        const resolvedUid = (result && 'uid' in result ? result.uid : parent.uid) ?? '';
        const resolvedInvitationId = result && 'invitationId' in result ? result.invitationId : null;

        contact = {
          firstName: parent.firstName,
          lastName: parent.lastName,
          displayName: formatFullName(parent, true),
          email: parent.email ?? '',
          phone: phoneNumberFormat(parent.phone),
          type: ContactType.FAMILY,
          relationship: 'parent',
          status,
          defaultOrganization: organization?.id ?? '',
          defaultLocation: location?.id ?? '',
          defaultRoom: room ?? '',
          organizations: [organization.id],
          locations: location?.id ? [location.id] : [],
          rooms: room ? [room] : [],
          allowLoginWithPin: true,
          uid: resolvedUid,
          ...(resolvedInvitationId ? { invitationId: resolvedInvitationId } : {}),
          invitationType: INVITATION_TYPES.parent,
          student: {
            // * student data for the enrollment invitation email
            // * the student hasn't been saved yet, so we need to use the form values and without the id
            displayName: formatFullName(dataToSave.studentAndParents?.student, true),
            firstName: dataToSave.studentAndParents?.student.firstName,
            lastName: dataToSave.studentAndParents?.student.lastName,
            organization: organization?.id ?? '',
          },
        };
      }
      contactsLocal.push(contact);
    }
    return contactsLocal;
  }, [dataToSave, location?.id, organization?.id]);

  const desiredStartDate = useMemo<string>(() => {
    if (!interestedFamily?.student?.desiredStartDate) return '';
    const date =
      dateFormatter(formatISOStringAsUtcDateOrNull(interestedFamily.student.desiredStartDate), 'YYYY-MM-DD') ?? '';
    return date;
  }, [interestedFamily]);

  const enrollmentStatus = useMemo<EnrollmentStatusEnum>(() => {
    const status = interestedFamily?.status as MarketplaceFilteredStatusType;
    return status ? (MARKETPLACE_STATUS_MAP[status] ?? EnrollmentStatusEnum.PENDING) : EnrollmentStatusEnum.PENDING;
  }, [interestedFamily]);

  const enrollmentSource = useMemo<EnrollmentSourceEnum>(() => {
    return interestedFamily?.revenueSource
      ? (MARKETPLACE_REVENUE_SOURCE_MAP[interestedFamily.revenueSource] ?? EnrollmentSourceEnum.PROVIDER)
      : EnrollmentSourceEnum.PROVIDER;
  }, [interestedFamily]);

  // only save new contacts. No need to save existing contacts
  const saveContacts = useCallback(async () => {
    const newContacts = contacts.filter((contact) => !contact.id);
    try {
      for (const contact of newContacts) {
        const docRef = await saveContact(organization.id, contact);
        contact.id = docRef.id;
      }
    } catch (error) {
      logError('Error while saving contacts', error);
    }
  }, [contacts, organization]);

  const saveStudentLocal = useCallback(async () => {
    const studentLocal = dataToSave.studentAndParents?.student;
    const parents = dataToSave.studentAndParents?.parents;
    const room = dataToSave.enrollmentInformation?.room ?? '';

    // strip room from the enrollment information, since we store it in the student
    const enrollmentInformation = omit(dataToSave.enrollmentInformation, 'room') ?? {};
    const isEnrolled = !!(
      enrollmentStatus === EnrollmentStatusEnum.ENROLLED ||
      (enrollmentInformation.automaticallyEnroll && isTodayOrBefore(enrollmentInformation.startDate))
    );

    if (!studentLocal || !parents?.length) return null;

    await saveContacts();
    const family: any = {};
    const familyMembers: string[] = [];

    contacts.forEach((contact) => {
      family[contact.id] = contact;
      familyMembers.push(contact.id);
      if (contact.uid) familyMembers.push(contact.uid);
    });

    const studentData: any = {
      firstName: studentLocal.firstName ?? '',
      lastName: studentLocal.lastName ?? '',
      displayName: formatFullName(studentLocal, true),
      birthday: studentLocal.birthday ? new Date(studentLocal.birthday) : null,
      gender: studentLocal.gender ?? '',
      defaultOrganization: organization.id,
      defaultLocation: location?.id ?? '',
      defaultRoom: room ?? '',
      organizations: [organization.id],
      locations: location?.id ? [location.id] : [],
      rooms: room ? [room] : [],
      family,
      familyMembers,
      source: enrollmentSource,
      enrollmentStatus: isEnrolled, // this should be deprecated, but still including for now
      enrollmentDate: new Date(enrollmentInformation.startDate ?? '').valueOf() ?? null,
      enrollment: {
        ...enrollmentInformation,
        status: isEnrolled ? EnrollmentStatusEnum.ENROLLED : enrollmentStatus,
        source: enrollmentSource,
      },
    };

    if (interestedFamily) {
      let notes = interestedFamily.student?.notes ?? '';
      if (notes && interestedFamily.student?.needs) notes += ' ' + interestedFamily.student.needs;
      if (notes && interestedFamily.student?.medical) notes += ' ' + interestedFamily.student.medical;

      studentData.allergies = interestedFamily.student?.allergies ?? '';
      studentData.medications = interestedFamily.student?.medications ?? '';
      studentData.doctorName = '';
      studentData.notes = notes;
      studentData.enrollment.interestedFamily = interestedFamily;
      studentData.marketplaceId = interestedFamily.student?.id ?? '';
      studentData.enrollment.scheduledDays = createScheduledDaysFromInterestedFamily(interestedFamily);
    }
    const docRef = await saveStudent(organization.id, studentData, { merge: false });
    return { ...studentData, id: docRef.id };
  }, [
    dataToSave,
    organization,
    location,
    saveContacts,
    contacts,
    interestedFamily,
    enrollmentStatus,
    enrollmentSource,
  ]);

  const shouldSaveToFirestore = useCallback(() => {
    return isSaving && Object.keys(dataToSave).length === 2 && isEmpty(errors);
  }, [isSaving, dataToSave, errors]);

  useEffect(() => {
    const saveLocal = async () => {
      try {
        const studentSavedLocal = await saveStudentLocal();
        logInfo('Should send enrollment status to MP?', { studentSavedLocal });
        if (studentSavedLocal?.enrollment?.interestedFamily?.id) {
          sendEnrollmentStatus(
            studentSavedLocal?.enrollment?.status,
            studentSavedLocal.enrollment.interestedFamily.id
          ).catch((error) => {
            logError('Error while sending enrollment status', error);
          });
        }
        dispatch(studentAdded(studentSavedLocal));
        gotoRouteByName(RouteNameEnum.STUDENT_ENROLLMENT, [{ name: 'studentId', value: studentSavedLocal.id }]);
      } catch (error) {
        logError('Error while saving student', error);
      } finally {
        setIsSaving(false);
        setIsDisabled(false);
      }
    };
    if (shouldSaveToFirestore()) {
      setIsDisabled(true);
      saveLocal();
    } else {
      if (isSaving) {
        setIsDisabled(false);
      }
    }
  }, [
    isSaving,
    organization,
    shouldSaveToFirestore,
    dataToSave,
    saveStudentLocal,
    gotoRouteByName,
    dispatch,
    sendEnrollmentStatus,
  ]);

  const onSave = useCallback(
    ({ data, errors }: OnSaveParamsType, fieldName: 'enrollmentInformation' | 'studentAndParents') => {
      if (!isSaving) return false;

      if (!isEmpty(errors)) {
        setIsSaving(false);
        setErrors((prev) => ({ ...prev, ...errors }));
        setDataToSave({});
      } else {
        setDataToSave((prev) => ({ ...prev, [fieldName]: data }));
      }
    },
    [isSaving]
  );

  return (
    <>
      <div className="rounded-lg bg-white p-4 shadow-md">
        <PageHeader pageName={'Add Student'} classes="add-student-page" />
        <h1 className="mb-4 text-2xl font-bold">{t('students.addStudentPageTitle')}</h1>
        <div className="mb-6 rounded-md border-2 border-gray-300 bg-gray-50 p-4">
          <StudentAndParentsForm
            isSaving={isSaving && !dataToSave.studentAndParents}
            onSave={(d) => onSave(d, 'studentAndParents')}
            marketplaceChildId={marketplaceChildId}
            onInterestedFamilySelected={(interestedFamilyLocal) => {
              setInterestedFamily(interestedFamilyLocal);
            }}
          />
        </div>
        <div className="mb-6 rounded-md border-2 border-gray-300 bg-gray-50 p-4">
          <EnrollmentInformationForm
            isSaving={isSaving && !dataToSave.enrollmentInformation}
            onSave={(d) => onSave(d, 'enrollmentInformation')}
            desiredStartDate={desiredStartDate}
            desiredRoomId={defaultRoomId}
          />
        </div>
        <CommandButtons />
      </div>
    </>
  );

  function CommandButtons() {
    return (
      <div className="ws-form-buttons" data-testid="as-form-btns">
        <Button
          primary={false}
          disabled={isDisabled}
          onClick={() => {
            goBack();
          }}
          data-testid="as-cancel-btn"
          preIcon={<CloseIcon className="size-6" data-testid="as-cancel-icon" />}
        >
          {t('common.cancel')}
        </Button>
        <Button
          primary
          loading={isDisabled}
          disabled={isDisabled}
          onClick={() => {
            setIsDisabled(true);
            setIsSaving(true);
            setErrors({});
          }}
          postIcon={<ArrowRightIcon className="size-6" data-testid="as-finish-icon" />}
          data-testid="as-finish-btn"
        >
          {t('common.addStudentAndContinue')}
        </Button>
      </div>
    );
  }
};

export default StudentAddPage;
